/**
 * Normalize API link_groups to fringe-first shape.
 * Shape: { [fringe_id]: { [link_group]: rowIds } }
 */
export function normalizeLinkGroupsFromAPI(linkGroups) {
  const formattedGroups = {};
  Object.entries(linkGroups || {}).forEach(([link_group, group]) => {
    const { swimlane_detail_sheets, line_item_detail_sheets, fringe_id } =
      group;
    const rowIds = [
      ...(swimlane_detail_sheets || []).map(id1 => id1.toString()),
      ...(line_item_detail_sheets || []).map(id1 => id1.toString()),
    ];
    if (!formattedGroups[fringe_id]) {
      formattedGroups[fringe_id] = {};
    }
    formattedGroups[fringe_id][link_group] = rowIds;
  });
  return formattedGroups;
}

/**
 * Apply a range checkbox change with non-overlapping enforcement.
 * When a row is checked in one range, it is removed from all other ranges.
 * @param {Object} prevState - Current rangeCheckedRows: { rangeId: { rowId: boolean } }
 * @param {string} rangeId - The range being modified
 * @param {string} rowId - The row being checked/unchecked
 * @param {boolean} isChecked - Whether the row is being checked or unchecked
 * @returns {Object} Updated rangeCheckedRows
 */
export function applyRangeCheckboxChange(prevState, rangeId, rowId, isChecked) {
  const next = {};
  for (const colId of Object.keys(prevState)) {
    if (colId === rangeId) {
      next[colId] = { ...prevState[colId], [rowId]: isChecked };
    } else if (isChecked) {
      const colCopy = { ...prevState[colId] };
      delete colCopy[rowId];
      next[colId] = colCopy;
    } else {
      next[colId] = { ...prevState[colId] };
    }
  }
  return next;
}

/**
 * Apply a select-all operation for a range with non-overlapping enforcement.
 * @param {Object} prevState - Current rangeCheckedRows
 * @param {string} rangeId - The range to select/deselect all
 * @param {boolean} isChecked - Whether to select or deselect all
 * @param {Array} filteredRows - Rows eligible for selection (must have .id)
 * @returns {Object} Updated rangeCheckedRows
 */
export function applyRangeSelectAll(
  prevState,
  rangeId,
  isChecked,
  filteredRows
) {
  const next = {};
  for (const colId of Object.keys(prevState)) {
    if (colId === rangeId) {
      const colChecked = {};
      if (isChecked) {
        filteredRows.forEach(row => {
          colChecked[row.id.toString()] = true;
        });
      }
      next[colId] = colChecked;
    } else if (isChecked) {
      const colCopy = { ...prevState[colId] };
      filteredRows.forEach(row => {
        delete colCopy[row.id.toString()];
      });
      next[colId] = colCopy;
    } else {
      next[colId] = { ...prevState[colId] };
    }
  }
  return next;
}

/**
 * Build the grouped payload for linking fringes from range state.
 * @param {Array} rangeColumns - Array of { id: string }
 * @param {Object} rangeCheckedRows - { rangeId: { rowId: boolean } }
 * @returns {{ groupPayload: Array, allIncludedIds: number[] }}
 */
export function buildGroupPayload(rangeColumns, rangeCheckedRows) {
  const groupPayload = [];
  const allIncludedIds = [];

  rangeColumns.forEach(col => {
    const checks = rangeCheckedRows[col.id] || {};
    const ids = Object.entries(checks)
      .filter(([, v]) => v === true)
      .map(([rowId]) => parseInt(rowId, 10));

    if (ids.length === 0) return;
    allIncludedIds.push(...ids);

    let groupId = col.id;
    if (groupId.startsWith('__new_')) {
      groupId = 'generated-uuid';
    }
    groupPayload.push({ [groupId]: ids });
  });

  return { groupPayload, allIncludedIds };
}
